import React, { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from 'react-dom';
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Search, ChevronUp, ChevronRight, Pencil, X, AlertTriangle, Plus, Trash2, FileText, ArrowUp, ArrowDown } from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { ErrorState, Skeleton } from "@/components/BookingShell";
import { formatPrice, formatDuration } from "@/lib/format";
import { CAT_LABEL } from "./Forms";
import { ServiceRules } from "@/components/ServiceRules";
import { groupServices } from "@/lib/serviceGroups";
import { ServiceWorkspace } from "@/components/ServiceWorkspace";

const CACHE_KEY = "pout.staff.services.openCat";

export function StaffServices() {
    return <ServiceWorkspace FormsSection={ServiceFormsSection}/>;
}

// Retained for a small, reversible navigation release.
// eslint-disable-next-line no-unused-vars
function LegacyStaffServices() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [openCat, setOpenCat] = useState(() => { try { return Number(sessionStorage.getItem(CACHE_KEY)) || null; } catch { return null; } });
    const [editing, setEditing] = useState(null); // full raw amelia service
    const [rules, setRules] = useState({});

    const load = () => { setError(null); setData(null); staffApi.services().then(setData).catch(setError); };
    useEffect(load, []);
    const loadRules = () => staffApi.catalogue().then((d) => setRules(d.rules)).catch((e) => toast.error(e.message));
    useEffect(() => { loadRules(); }, []);

    useEffect(() => { try { if (openCat) sessionStorage.setItem(CACHE_KEY, String(openCat)); else sessionStorage.removeItem(CACHE_KEY); } catch {} }, [openCat]);

    const employees = data?.employees || [];
    const angelaId = employees[0]?.id;

    const grouped = useMemo(() => {
        if (!data) return [];
        const cats = data.categories || [];
        const services = data.services || [];
        const q = search.trim().toLowerCase();
        const byCatId = new Map();
        cats.forEach((c) => byCatId.set(c.id, { ...c, services: [] }));
        services.forEach((s) => {
            if (q) {
                const hay = `${s.name || ""} ${s.description || ""}`.toLowerCase();
                if (!hay.includes(q)) return;
            }
            if (byCatId.has(s.categoryId)) byCatId.get(s.categoryId).services.push(s);
            else byCatId.set(s.categoryId || 0, { id: s.categoryId || 0, name: s.categoryName || "Uncategorised", services: [s] });
        });
        return Array.from(byCatId.values())
            .filter((c) => c.services.length > 0)
            .sort((a, b) => (a.position || 0) - (b.position || 0));
    }, [data, search]);

    const openEdit = async (serviceId) => {
        try {
            const svc = await staffApi.getService(serviceId);
            setEditing(svc);
        } catch (e) { toast.error(e.message || "Could not load service"); }
    };

    const onSaved = () => { setEditing(null); load(); loadRules(); };

    return (
        <div className="space-y-5" data-testid="staff-services">
            <div>
                <h1 className="font-serif font-light text-3xl md:text-4xl text-white">Services</h1>
                <p className="text-sm text-white/60 mt-1">
                    Manage treatments, pricing, durations and deposits.
                </p>
            </div>

            {/* Search — matches Forms page pill style */}
            <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" strokeWidth={1.5} />
                <input
                    data-testid="services-search"
                    placeholder="Search services"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full h-11 pl-10 pr-4 rounded-full bg-white text-navy placeholder-slate-400 text-sm border border-slate-200 focus:border-gold outline-none shadow-sm"
                />
            </div>

            {error && <ErrorState error={error} onRetry={load} />}
            {!data && !error && <Skeleton className="h-64" />}

            {data && grouped.length === 0 && (
                <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center text-slate-500 shadow-sm" data-testid="services-empty">
                    No services match.
                </div>
            )}

            {data && grouped.map((cat) => {
                const isOpen = openCat === cat.id || !!search;
                return (
                    <section
                        key={cat.id}
                        className="rounded-2xl bg-[#0E1746] surface-highlight shadow-lg overflow-hidden"
                        data-testid={`category-${cat.id}`}
                    >
                        <button
                            onClick={() => setOpenCat(isOpen && !search ? null : cat.id)}
                            data-testid={`category-toggle-${cat.id}`}
                            className="w-full flex items-center justify-between gap-3 px-4 py-4 hover:bg-white/[0.05] transition-colors text-left"
                        >
                            <div className="min-w-0 flex-1">
                                <div className="text-white font-semibold tracking-tight text-xl md:text-[22px] truncate">
                                    {cat.name}
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 mt-1">
                                    {cat.services.length} service{cat.services.length === 1 ? "" : "s"}
                                </div>
                            </div>
                            {isOpen
                                ? <ChevronUp className="h-5 w-5 text-gold shrink-0" />
                                : <ChevronRight className="h-5 w-5 text-gold shrink-0" />}
                        </button>
                        {isOpen && (
                            <div className="px-3 pb-3 pt-1 space-y-3">
                                {groupServices(cat.services, rules).map((g) => !g.hasVariants
                                    ? <ServiceRow key={g.parent.id} service={g.parent} onOpen={() => openEdit(g.parent.id)} />
                                    : <details key={g.parent.id} className="rounded-xl bg-white p-3 text-navy">
                                        <summary className="cursor-pointer min-h-11 font-semibold">{g.parent.name} · From {formatPrice(g.fromPrice)} · {g.options.length} options</summary>
                                        <div className="space-y-2">{g.options.map((s) => <ServiceRow key={s.id} service={{ ...s, name: rules[String(s.id)]?.option_label || s.name }} onOpen={() => openEdit(s.id)} />)}</div>
                                      </details>)}
                            </div>
                        )}
                    </section>
                );
            })}

            {editing && (
                <ServiceEditModal
                    service={editing}
                    services={data.services}
                    onRulesSaved={loadRules}
                    categories={data.categories}
                    angelaId={angelaId}
                    onClose={() => { setEditing(null); load(); loadRules(); }}
                    onSaved={onSaved}
                />
            )}
        </div>
    );
}

function ServiceRow({ service: s, onOpen }) {
    const active = s.status === "visible";
    const depositLabel = Number(s.deposit) > 0 && s.depositPayment && s.depositPayment !== "disabled"
        ? (s.depositPayment === "percentage" ? `${s.deposit}% deposit` : `${formatPrice(s.deposit)} deposit`)
        : null;
    return (
        <button
            type="button"
            onClick={onOpen}
            data-testid={`service-${s.id}`}
            className="w-full text-left rounded-xl bg-white text-navy shadow-sm flex items-start gap-3 p-4 hover:bg-slate-50/70 transition-colors"
        >
            <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                    <div className="text-navy font-semibold text-[15px] leading-snug break-words pr-6">
                        {s.name}
                    </div>
                    <span className={`shrink-0 inline-flex text-[10px] font-semibold tracking-[0.12em] uppercase px-2.5 py-1 rounded-full border ${
                        active
                            ? "border-emerald-500/50 text-emerald-600 bg-emerald-500/[0.08]"
                            : "border-slate-400/50 text-slate-500 bg-slate-500/[0.06]"
                    }`}>
                        {active ? "Active" : (s.status || "Hidden")}
                    </span>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-slate-500">
                    <span className="font-semibold text-gold" data-testid={`service-${s.id}-price`}>
                        {formatPrice(s.price)}
                    </span>
                    <span className="text-slate-300">·</span>
                    <span>{formatDuration(s.duration)}</span>
                    {depositLabel && (
                        <>
                            <span className="text-slate-300">·</span>
                            <span className="text-gold/90" data-testid={`service-${s.id}-deposit`}>
                                {depositLabel}
                            </span>
                        </>
                    )}
                </div>
            </div>
            <span
                aria-hidden
                className="mt-0.5 shrink-0 h-8 w-8 rounded-full border border-slate-200 text-navy/70 flex items-center justify-center"
                data-testid={`service-edit-${s.id}`}
            >
                <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
            </span>
        </button>
    );
}

/* eslint-disable-next-line no-unused-vars */
function _DeprecatedChip({ label, value, tone }) {
    return (
        <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 ${tone === "gold" ? "border-gold/40 text-gold" : "border-white/10 text-white/70"}`}>
            <span className="uppercase tracking-[0.14em] text-[9px] text-white/40">{label}</span>
            <span>{value}</span>
        </span>
    );
}

function ServiceEditModal({ service, services, categories, angelaId, onClose, onSaved, onRulesSaved }) {
    const [f, setF] = useState({
        name: service.name || "",
        description: service.description || "",
        price: Number(service.price || 0),
        duration: Number(service.duration || 0),
        categoryId: service.categoryId || (categories[0]?.id ?? null),
        status: service.status || "visible",
        deposit: Number(service.deposit || 0),
        depositPayment: service.depositPayment || "disabled",
        depositPerPerson: !!service.depositPerPerson,
        minCapacity: Number(service.minCapacity || 1),
        maxCapacity: Number(service.maxCapacity || 1),
    });
    const [busy, setBusy] = useState(false);
    const [confirm, setConfirm] = useState(false);
    const [panel, setPanel] = useState("pricing");
    const ruleEditor = useRef(null);
    const set = (k) => (v) => setF({ ...f, [k]: v });

    // Providers preserved from current service. If empty (shouldn't happen), inject Angela.
    const providers = (service.providers && service.providers.length ? service.providers : (angelaId ? [angelaId] : []));

    const doSave = async () => {
        if (busy) return; setBusy(true);
        let detailsSaved = false;
        try {
            const patch = {
                name: f.name.trim(),
                description: f.description,
                price: Number(f.price),
                duration: Number(f.duration),
                categoryId: Number(f.categoryId),
                status: f.status,
                deposit: Number(f.deposit),
                depositPayment: f.depositPayment,
                depositPerPerson: !!f.depositPerPerson,
                providers,
            };
            const res = await staffApi.updateService(service.id, patch);
            detailsSaved = true;
            await ruleEditor.current.save();
            toast.success(`Saved — updated ${res.changed_fields.length} field${res.changed_fields.length === 1 ? "" : "s"}`);
            onSaved();
        } catch (e) { toast.error((detailsSaved ? "Service details saved, but grouping/rules were not saved: " : "") + (e.message || "Could not save changes")); }
        finally { setBusy(false); setConfirm(false); }
    };

    return (
        <div className="service-editor fixed inset-0 z-[100] bg-black/70 overflow-y-auto" data-testid="service-edit-modal">
            <div className="min-h-full flex items-start justify-center py-8 px-4">
                <div className="w-full max-w-lg rounded-3xl glass-strong p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.24em] text-gold/85">Edit service #{service.id}</div>
                            <h2 className="font-serif text-2xl text-white mt-1">{service.name}</h2>
                        </div>
                        <button onClick={onClose} data-testid="edit-close" className="min-h-11 min-w-11 rounded-full text-white/60 flex items-center justify-center"><X className="h-4 w-4"/></button>
                    </div>

                    <nav aria-label="Service editor sections" className="grid grid-cols-2 sm:grid-cols-4 gap-2">{[["pricing","Pricing"],["details","Details"],["rules","Booking rules"],["forms","Forms"]].map(([key,label])=><button type="button" key={key} aria-pressed={panel===key} onClick={()=>setPanel(key)} className={`min-h-11 rounded-lg text-sm border ${panel===key?"border-gold text-gold bg-gold/10":"border-white/20 text-white/70"}`}>{label}</button>)}</nav>
                    <div hidden={panel !== "details"} className="space-y-4">
                    <Field label="Name" testId="edit-name"><input value={f.name} onChange={(e) => set("name")(e.target.value)} className={inp} data-testid="edit-name-input" /></Field>
                    <Field label="Description" testId="edit-description"><textarea rows={3} value={f.description} onChange={(e) => set("description")(e.target.value)} className={`${inp} resize-none`} data-testid="edit-description-input" /></Field>
                    </div>
                    <div hidden={panel !== "details"} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Category" testId="edit-category">
                            <select value={f.categoryId ?? ""} onChange={(e) => set("categoryId")(Number(e.target.value))} className={inp} data-testid="edit-category-input">
                                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Visibility" testId="edit-status">
                            <select value={f.status} onChange={(e) => set("status")(e.target.value)} className={inp} data-testid="edit-status-input">
                                <option value="visible">Visible</option>
                                <option value="hidden">Hidden</option>
                                <option value="disabled">Disabled</option>
                            </select>
                        </Field>
                    </div>
                    <div hidden={!["pricing","rules"].includes(panel)}>
                    <ServiceRules pricingFields={<>
                    <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Price (£)" testId="edit-price"><input type="number" step="0.01" value={f.price} onChange={(e) => set("price")(e.target.value)} className={inp} data-testid="edit-price-input" /></Field>
                        <Field label="Duration (min)" testId="edit-duration">
                            <input type="number" value={Math.round(f.duration/60)} onChange={(e) => set("duration")(Number(e.target.value) * 60)} className={inp} data-testid="edit-duration-input" />
                        </Field>
                    </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Deposit amount" testId="edit-deposit"><input type="number" step="0.01" value={f.deposit} onChange={(e) => set("deposit")(e.target.value)} className={inp} data-testid="edit-deposit-input" /></Field>
                        <Field label="Deposit type" testId="edit-deposit-payment">
                            <select value={f.depositPayment} onChange={(e) => set("depositPayment")(e.target.value)} className={inp} data-testid="edit-deposit-payment-input">
                                <option value="disabled">Disabled</option>
                                <option value="fixed">Fixed</option>
                                <option value="percentage">Percentage</option>
                            </select>
                        </Field>
                    </div>
                    </>} embedded ref={ruleEditor} panel={panel} serviceId={service.id} services={services} onSaved={onRulesSaved} />

                    </div>

                    <div hidden={panel !== "forms"}><ServiceFormsSection serviceId={service.id} /></div>

                    <div className="flex gap-2 pt-2" hidden={panel === "forms"}>
                        <button onClick={() => setConfirm(true)} disabled={busy} data-testid="edit-save" className="flex-1 h-11 rounded-full bg-gold text-navy font-semibold text-sm disabled:opacity-40">Save changes</button>
                        <button onClick={onClose} data-testid="edit-cancel" className="h-11 rounded-full border hairline text-white/70 px-5 text-sm">Cancel</button>
                    </div>
                </div>
            </div>

            {confirm && (
                <div className="fixed inset-0 z-[110] bg-black/80 flex items-center justify-center px-4" data-testid="edit-confirm">
                    <div className="w-full max-w-sm rounded-2xl glass-strong p-5">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="h-5 w-5 text-amber-300 mt-0.5" strokeWidth={1.5} />
                            <div>
                                <div className="font-serif text-lg text-white">Save changes?</div>
                                <p className="mt-1 text-xs text-white/60">This updates the live service in your booking system. Change is reversible via another edit.</p>
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end gap-2">
                            <button onClick={() => setConfirm(false)} className="h-10 rounded-full border hairline text-white/70 px-4 text-sm" data-testid="edit-confirm-cancel">Cancel</button>
                            <button onClick={doSave} disabled={busy} data-testid="edit-confirm-save" className="h-10 rounded-full bg-gold text-navy px-4 text-sm font-semibold disabled:opacity-40">{busy ? "Saving..." : "Yes, save"}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const inp = "w-full bg-transparent border hairline rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-gold";

function Field({ label, children, testId }) {
    return (
        <label className="block" data-testid={`${testId}-field`}>
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">{label}</div>
            {children}
        </label>
    );
}


/* ==========================================================================
 * ServiceFormsSection — form assignments inside the service edit modal.
 * ======================================================================= */
const VALIDITY_OPTIONS = [
    { value: "",   label: "Appointment only" },
    { value: "3",  label: "3 months" },
    { value: "6",  label: "6 months" },
    { value: "12", label: "12 months" },
    { value: "custom", label: "Custom…" },
];
const COMPLETION_OPTIONS = [
    { value: "every",       label: "Every appointment" },
    { value: "if_no_valid", label: "Only if no valid form exists" },
    { value: "renew_after", label: "Renew after N months" },
];
const DELIVERY_OPTIONS = [
    { value: "booking", label: "Automatically after booking" },
    { value: "checkout", label: "Automatically when staff check out the appointment" },
    { value: "manual", label: "Send manually" },
];

export function ServiceFormsSection({ serviceId }) {
    const [assignments, setAssignments] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [error, setError] = useState(null);
    const [addOpen, setAddOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = () => {
        setError(null);
        staffApi.listServiceForms(serviceId).then((r) => setAssignments(r.assignments || [])).catch(setError);
    };
    useEffect(load, [serviceId]);
    useEffect(() => {
        staffApi.listForms({ status: "published" }).then((r) => setTemplates(r.forms || [])).catch(() => setTemplates([]));
    }, []);

    const usedIds = new Set((assignments || []).map((a) => a.form_template_id));
    const available = templates.filter((t) => !usedIds.has(t.id) && t.status === "published");

    const doAdd = async (form_template_id, required, completion_mode, validity_months, delivery_trigger) => {
        setBusy(true);
        try {
            await staffApi.assignFormToService(serviceId, { form_template_id, required, completion_mode, validity_months, delivery_trigger });
            setAddOpen(false);
            load();
        } catch (e) { toast.error(e?.message || "Assign failed"); }
        finally { setBusy(false); }
    };
    const doPatch = async (assignmentId, patch) => {
        try { await staffApi.patchServiceFormAssignment(serviceId, assignmentId, patch); load(); }
        catch (e) { toast.error(e?.message || "Update failed"); }
    };
    const doRemove = async (assignmentId, name) => {
        if (!window.confirm(`Remove "${name}" from this service? Customers booking this service will no longer be asked to complete it.`)) return;
        try { await staffApi.removeServiceFormAssignment(serviceId, assignmentId); load(); }
        catch (e) { toast.error(e?.message || "Remove failed"); }
    };
    const doReorder = async (id, dir) => {
        const ids = (assignments || []).map((a) => a.id);
        const i = ids.indexOf(id); const j = i + dir;
        if (i < 0 || j < 0 || j >= ids.length) return;
        const next = [...ids]; const [x] = next.splice(i, 1); next.splice(j, 0, x);
        try { const r = await staffApi.reorderServiceFormAssignments(serviceId, next); setAssignments(r.assignments || []); }
        catch (e) { toast.error(e?.message || "Reorder failed"); }
    };

    return (
        <div className="rounded-xl border hairline p-3 bg-white/[0.03] space-y-3" data-testid="service-forms-section">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-gold" />
                    <div className="text-sm text-white/85">Assigned forms</div>
                </div>
                <button onClick={() => setAddOpen(true)} disabled={available.length === 0} data-testid="sf-assign-open"
                        className="h-9 px-3 rounded-full border hairline text-[11px] uppercase tracking-[0.14em] text-white/80 hover:text-gold hover:border-gold/60 disabled:opacity-40 inline-flex items-center gap-1">
                    <Plus className="h-3 w-3" /> Assign
                </button>
            </div>
            {error && <ErrorState error={error} onRetry={load} />}
            {!assignments && !error && <Skeleton className="h-16" />}
            {assignments && assignments.length === 0 && (
                <p className="text-sm text-white/60">No forms assigned. Customers will receive their booking summary without a form request. Add any consultation, medical, aftercare or custom forms this service needs.</p>
            )}
            {assignments && assignments.length > 0 && (
                <ul className="space-y-2">
                    {assignments.map((a, idx) => (
                        <li key={a.id} className="rounded-lg border hairline p-2.5 bg-white/[0.02]" data-testid={`sf-item-${a.id}`}>
                            <div className="flex items-start gap-2 min-w-0">
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm text-white break-words">{a.template?.name || "(deleted form)"}</div>
                                    <div className="text-[10px] uppercase tracking-[0.14em] text-white/45 mt-0.5">
                                        {CAT_LABEL[a.template?.category] || a.template?.category || "—"} · v{a.template?.latest_version_number || "?"}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <button onClick={() => doReorder(a.id, -1)} disabled={idx === 0} className="h-7 w-7 rounded-full border hairline text-white/60 disabled:opacity-30 inline-flex items-center justify-center" data-testid={`sf-up-${a.id}`}><ArrowUp className="h-3 w-3" /></button>
                                    <button onClick={() => doReorder(a.id, 1)} disabled={idx === assignments.length - 1} className="h-7 w-7 rounded-full border hairline text-white/60 disabled:opacity-30 inline-flex items-center justify-center" data-testid={`sf-down-${a.id}`}><ArrowDown className="h-3 w-3" /></button>
                                    <button onClick={() => doRemove(a.id, a.template?.name || "form")} className="h-7 w-7 rounded-full border hairline text-red-300/70 inline-flex items-center justify-center" data-testid={`sf-remove-${a.id}`}><Trash2 className="h-3 w-3" /></button>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                                <label className="sm:col-span-3 text-xs text-white/70">When to send
                                    <select aria-label={`When to send ${a.template?.name || 'form'}`} value={a.delivery_trigger || "manual"} onChange={(e) => doPatch(a.id, { delivery_trigger: e.target.value })}
                                        className="mt-1 w-full bg-white text-navy border hairline rounded-md px-3 py-3">
                                        {DELIVERY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                </label>
                                <label className="text-[10px] uppercase tracking-[0.14em] text-white/40">Requirement
                                    <select value={a.required ? "1" : "0"} onChange={(e) => doPatch(a.id, { required: e.target.value === "1" })}
                                            data-testid={`sf-required-${a.id}`}
                                            className="mt-1 w-full bg-navy border hairline rounded-md text-xs text-white px-2 py-1.5">
                                        <option value="1">Required</option>
                                        <option value="0">Optional</option>
                                    </select>
                                </label>
                                <label className="text-[10px] uppercase tracking-[0.14em] text-white/40">Completion
                                    <select value={a.completion_mode === "manual" ? "every" : a.completion_mode} onChange={(e) => doPatch(a.id, { completion_mode: e.target.value })}
                                            data-testid={`sf-mode-${a.id}`}
                                            className="mt-1 w-full bg-navy border hairline rounded-md text-xs text-white px-2 py-1.5">
                                        {COMPLETION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                </label>
                                <label className="text-[10px] uppercase tracking-[0.14em] text-white/40">Valid for
                                    <input type="number" min="0" placeholder="months (blank = every)" value={a.validity_months ?? ""}
                                           onChange={(e) => doPatch(a.id, { validity_months: e.target.value ? Number(e.target.value) : null })}
                                           data-testid={`sf-validity-${a.id}`}
                                           className="mt-1 w-full bg-transparent border hairline rounded-md text-xs text-white px-2 py-1.5" />
                                </label>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
            {addOpen && (
                <AssignFormPopover
                    available={available}
                    onCancel={() => setAddOpen(false)}
                    onAdd={doAdd}
                    busy={busy}
                />
            )}
            <div className="text-[10px] text-white/40">
                Only <strong className="text-white/60">published</strong> forms can be assigned. Manage forms in the <Link to="/staff/forms" className="text-gold">Forms</Link> section.
            </div>
        </div>
    );
}

export function AssignFormPopover({ available, onCancel, onAdd, busy }) {
    const dialog = useRef(null);
    const cancelRef = useRef(onCancel);
    const busyRef = useRef(busy);
    cancelRef.current = onCancel;
    busyRef.current = busy;
    useEffect(() => {
        const previous = document.activeElement;
        const overflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        dialog.current?.focus();
        const handleKey = event => {
            if (event.key === 'Escape') { event.stopPropagation(); if (!busyRef.current) cancelRef.current(); }
            if (event.key !== 'Tab') return;
            const nodes = [...dialog.current.querySelectorAll('button:not(:disabled), input, select, [tabindex="0"]')];
            const first = nodes[0], last = nodes[nodes.length - 1];
            if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last?.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        };
        const node = dialog.current;
        node.addEventListener('keydown', handleKey);
        return () => { node.removeEventListener('keydown', handleKey); document.body.style.overflow = overflow; previous?.focus(); };
    }, []);
    const [q, setQ] = useState("");
    const [category, setCategory] = useState('all');
    const [selected, setSelected] = useState("");
    const settings = useRef(null);
    useEffect(() => { if (selected) settings.current?.scrollIntoView?.({block:'nearest'}); }, [selected]);
    const [required, setRequired] = useState(true);
    const [mode, setMode] = useState("every");
    const [validity, setValidity] = useState("");
    const [delivery, setDelivery] = useState("manual");
    const categories = [...new Set(available.map(t => t.category || 'other'))].sort((a,b) => (CAT_LABEL[a] || a).localeCompare(CAT_LABEL[b] || b));
    const filtered = available.filter(t => (category === 'all' || (t.category || 'other') === category) && (t.name || '').toLowerCase().includes(q.toLowerCase()));
    const submit = () => {
        if (!selected) return;
        const v = validity === "" ? null : Number(validity);
        onAdd(selected, required, mode, v, delivery);
    };
    return createPortal(
        <div style={{position:'fixed',inset:0,zIndex:10000,background:'#0009',display:'flex',alignItems:'center',justifyContent:'center',padding:12}}>
        <div ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="assign-form-title" style={{background:'#101a36',color:'white',width:'100%',maxWidth:720,maxHeight:'calc(100dvh - 24px)',display:'flex',flexDirection:'column',borderRadius:20,overflow:'hidden',border:'1px solid #bf9f5680'}} data-testid="sf-assign-popover">
            <header className="p-4 flex justify-between items-center gap-3 border-b border-white/15">
                <h2 id="assign-form-title" className="text-xl text-white">Assign a form</h2>
                <button type="button" aria-label="Close form picker" disabled={busy} onClick={onCancel} className="p-2 text-gold"><X /></button>
            </header>
            <div className="p-4 overflow-y-auto space-y-4" style={{minHeight:0}}>
            <label className="block text-sm text-white">Search forms
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search available forms"
                   data-testid="sf-assign-search"
                   className="mt-1 w-full bg-white text-navy border hairline rounded-md p-3 text-base" /></label>
            <label className="block text-sm text-white">Category
                <select value={category} onChange={e => setCategory(e.target.value)} className="mt-1 w-full bg-white text-navy rounded-md p-3">
                    <option value="all">All categories</option>
                    {categories.map(c => <option key={c} value={c}>{CAT_LABEL[c] || c}</option>)}
                </select>
            </label>
            <div className="space-y-3">
                {filtered.length === 0 && <p className="text-[11px] text-white/50">No available published forms match.</p>}
                {categories.map(c => { const forms = filtered.filter(t => (t.category || 'other') === c); return forms.length ? <section key={c}>
                    <h3 className="text-gold text-sm mb-2">{CAT_LABEL[c] || c}</h3>
                    {forms.map((t) => (
                    <button key={t.id} onClick={() => setSelected(t.id)}
                            aria-pressed={selected === t.id}
                            data-testid={`sf-pick-${t.id}`}
                            className={`w-full mb-2 text-left rounded-md border p-3 text-sm ${selected === t.id ? "border-gold bg-gold/10 text-white" : "hairline text-white/80 hover:border-gold/40"}`}>
                        <div>{t.name}</div>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-white/40">{CAT_LABEL[t.category] || t.category} · v{t.latest_version_number}</div>
                    </button>
                ))}</section> : null; })}
            </div>
            {selected && (
                <div ref={settings} className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <p className="sm:col-span-3 text-sm text-gold">Selected: {available.find(t => t.id === selected)?.name}</p>
                    <label className="sm:col-span-3 text-xs text-white/70">When to send
                        <select aria-label="When to send new form" value={delivery} onChange={(e) => setDelivery(e.target.value)} className="mt-1 w-full bg-white text-navy border hairline rounded-md px-3 py-3">
                            {DELIVERY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </label>
                    <label className="text-[10px] uppercase tracking-[0.14em] text-white/40">Requirement
                        <select value={required ? "1" : "0"} onChange={(e) => setRequired(e.target.value === "1")}
                                className="mt-1 w-full bg-navy border hairline rounded-md text-xs text-white px-2 py-1.5" data-testid="sf-new-required">
                            <option value="1">Required</option>
                            <option value="0">Optional</option>
                        </select>
                    </label>
                    <label className="text-[10px] uppercase tracking-[0.14em] text-white/40">Completion
                        <select value={mode} onChange={(e) => setMode(e.target.value)}
                                className="mt-1 w-full bg-navy border hairline rounded-md text-xs text-white px-2 py-1.5" data-testid="sf-new-mode">
                            {COMPLETION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </label>
                    <label className="text-[10px] uppercase tracking-[0.14em] text-white/40">Valid for
                        <select value={validity} onChange={(e) => setValidity(e.target.value)}
                                className="mt-1 w-full bg-navy border hairline rounded-md text-xs text-white px-2 py-1.5" data-testid="sf-new-validity">
                            <option value="">Appointment only</option>
                            <option value="3">3 months</option>
                            <option value="6">6 months</option>
                            <option value="12">12 months</option>
                        </select>
                    </label>
                </div>
            )}
            </div>
            <div className="flex gap-2 p-4 border-t border-white/15 shrink-0">
                <button onClick={onCancel} disabled={busy} className="flex-1 h-10 rounded-full border hairline text-white/70 text-xs uppercase tracking-[0.14em]">Cancel</button>
                <button onClick={submit} disabled={!selected || busy} data-testid="sf-assign-submit"
                        className="flex-1 h-10 rounded-full bg-gold text-navy font-semibold text-xs uppercase tracking-[0.14em] disabled:opacity-40">{busy ? 'Assigning…' : 'Assign'}</button>
            </div>
        </div>
        </div>, document.body
    );
}
